import { useState, useEffect, useCallback } from 'react'
import ComponentsView from '../components/ComponentsView'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import AgentBlastRadius from '../components/AgentBlastRadius'
import { useCounterfactualExposure } from '../hooks/useCounterfactualExposure'
import {
  Box, Typography, Card, CardContent, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Alert, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Stack, Divider, Select, MenuItem,
  FormControl, InputLabel, Tabs, Tab,
  TablePagination,
} from '@mui/material'
import {
  Search, Refresh, SmartToy, InfoOutlined, CheckCircle, Cancel,
  MergeType, AccessTime,
} from '@mui/icons-material'
import { analyticsApi } from '../services/api'
import { RootState } from '../store'

const statusColor = (status: string) => {
  if (status === 'active') return 'success'
  if (status === 'retired') return 'default'
  if (status === 'merged') return 'info'
  return 'default'
}

const statusIcon = (status: string) => {
  if (status === 'active') return <CheckCircle fontSize="small" />
  if (status === 'retired') return <Cancel fontSize="small" />
  if (status === 'merged') return <MergeType fontSize="small" />
  return null
}

const matchTypeLabel: Record<string, string> = {
  exact: 'Exact',
  // Matched on the agent's displayed name because the scanner extracted no
  // code identifier for it — see _identity_token in identity_resolver.py.
  exact_by_name: 'Exact (by name)',
  fuzzy_name_changed: 'Name changed',
  fuzzy_file_moved: 'File moved',
  fuzzy_file_moved_by_name: 'File moved (by name)',
  fuzzy_restructured: 'Restructured',
  new: 'New',
  migrated: 'Migrated',
}

const matchTypeColor = (type: string): 'success' | 'warning' | 'info' | 'default' => {
  if (type === 'exact' || type === 'exact_by_name') return 'success'
  if (type === 'new' || type === 'migrated') return 'info'
  return 'warning'
}

// Fallback only: frameworks that happen to be spec-only today, used when
// a registry row predates AR5 (definition_source column) and hasn't been
// rescanned yet, so `agent.definition_source` is still null. Once the row
// has a real definition_source value that always wins — this list is
// deliberately incomplete (e.g. crewai_spec agents share the "crewai"
// framework family with code-mode ones, so no framework-based heuristic
// can distinguish them). Source-of-truth is scanner-client's
// definition_source field, not this list.
const SPEC_FRAMEWORKS = new Set([
  'claude_code',
  'anthropic_skills',
])

// AR4 — drift verdict swatch colors. Mirrors LensLegend in MeshView.
const driftVerdictColor = (verdict: string | null | undefined): string => {
  switch (verdict) {
    case 'CRITICAL_DRIFT': return '#d32f2f'
    case 'MATERIAL_DRIFT': return '#fb8c00'
    case 'COSMETIC_DRIFT': return '#ffc107'
    case 'REBASELINE': return '#1976d2'
    default: return '#9e9e9e'
  }
}


/**
 * AR2 — connectivity chips for the registry row. Renders presence-based
 * chips for MCP server/client + A2A server/client and a count chip for
 * delegations. Compact dash when the agent has no connections (lots of
 * agents are isolated leaves; chip-soup would drown the meaningful ones).
 */
function ConnectivityChips({ agent }: { agent: any }) {
  const chips: Array<{ key: string; label: string; color: 'primary' | 'secondary' | 'warning' | 'default' }> = []
  if (agent.has_mcp_server) chips.push({ key: 'mcp_s', label: 'MCP host', color: 'warning' })
  if (agent.has_mcp_client) chips.push({ key: 'mcp_c', label: 'MCP client', color: 'default' })
  if (agent.has_a2a_server) chips.push({ key: 'a2a_s', label: 'A2A host', color: 'warning' })
  if (agent.has_a2a_client) chips.push({ key: 'a2a_c', label: 'A2A client', color: 'default' })
  if (agent.delegates_to_count && agent.delegates_to_count > 0) {
    chips.push({ key: 'del', label: `Delegates ${agent.delegates_to_count}`, color: 'secondary' })
  }
  if (chips.length === 0) {
    return <Typography variant="caption" color="text.disabled">—</Typography>
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {chips.map((c) => (
        <Chip
          key={c.key}
          label={c.label}
          size="small"
          variant="outlined"
          color={c.color}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      ))}
    </Stack>
  )
}


// Real signal when present (every row seen by a post-AR5 scanner has it);
// falls back to the framework heuristic only for rows not yet rescanned.
const isSpecDefined = (agent: { definition_source?: string | null; framework?: string | null }): boolean =>
  agent.definition_source != null
    ? agent.definition_source !== 'code'
    : !!agent.framework && SPEC_FRAMEWORKS.has(agent.framework)

export default function AgentRegistryPage() {
  const navigate = useNavigate()
  // ARIAS Exposure. Keyed on `agent_id_hint`, which is the only identity the
  // two sides share — the registry's own `id` is a uuid the exposure endpoint
  // has never seen. The hint is a WITHIN-scan key (it embeds the definition's
  // line number), and the registry is cross-scan, so a stored hint can be
  // stale against the current scan. A miss therefore renders as "not
  // computed" rather than as a zero radius — see AgentBlastRadius.
  const { byHint: exposureByHint, loading: exposureLoading,
          notEntitled: exposureRefused } = useCounterfactualExposure()
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id

  const [agents, setAgents] = useState<any[]>([])
  const [count, setCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [frameworkFilter, setFrameworkFilter] = useState('')
  const [platformFilter, setPlatformFilter] = useState('')
  const [missingIntentOnly, setMissingIntentOnly] = useState(false)
  // Agents and the knowledge layer are two answers to the same question —
  // what do I have — so they belong on one surface rather than two pages.
  const [view, setView] = useState<'agents' | 'components'>('agents')
  const [selected, setSelected] = useState<any>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailData, setDetailData] = useState<any>(null)

  const fetchAgents = useCallback(async () => {
    if (!tenantId) return
    setLoading(true)
    setError('')
    try {
      // GET /registry/{tenant} is paginated and defaults to limit=25, so this
      // used to render the first 25 agents while the header reported the true
      // count from /count — a tenant with 50 agents saw "50" above a table of
      // 25. Everything below is derived client-side from `agents`: the search
      // box, the framework and platform dropdowns, and missingIntentCount. A
      // short page therefore did not just truncate the table, it made search
      // miss agents and left options out of the filters entirely.
      //
      // Pull every page instead of paginating the table, so all of that
      // client-side logic keeps working over the full set. 200 is the API's
      // ceiling (Query(25, le=200)), so this is one request for any realistic
      // tenant and stays correct past that.
      const PAGE_SIZE = 200
      const params = { status: statusFilter || undefined }
      const all: any[] = []
      let total = Infinity

      while (all.length < total) {
        const res = await analyticsApi.getAgentRegistry(tenantId, {
          ...params, limit: PAGE_SIZE, offset: all.length,
        })
        const entries = res.data?.entries || []
        total = res.data?.total ?? entries.length
        all.push(...entries)
        // Guard: an empty page with more claimed would otherwise spin forever.
        if (entries.length === 0) break
      }

      const countRes = await analyticsApi.getAgentRegistryCount(tenantId)
      setAgents(all)
      setCount(countRes.data?.count ?? null)
    } catch (e: any) {
      setError('Failed to load agent registry')
    } finally {
      setLoading(false)
    }
  }, [tenantId, statusFilter])

  useEffect(() => { fetchAgents() }, [fetchAgents])
  // Any filter change reshapes `filtered`. Holding a stale page number then
  // renders an empty table, which reads as "no agents match" rather than
  // "you are past the end".
  useEffect(() => {
    setPage(0)
  }, [search, statusFilter, frameworkFilter, platformFilter, missingIntentOnly])

  const openDetail = async (agent: any) => {
    setSelected(agent)
    setDetailData(null)
    setDetailLoading(true)
    try {
      const res = await analyticsApi.getAgentRegistryEntry(tenantId!, agent.id)
      setDetailData(res.data)
    } catch {
      setDetailData(agent)
    } finally {
      setDetailLoading(false)
    }
  }

  const frameworks = [...new Set(agents.map((a: any) => a.framework).filter(Boolean))]
  const platforms = [...new Set(agents.map((a: any) => a.runtime_platform).filter(Boolean))]

  const filtered = agents.filter((a: any) => {
    const name = (a.display_name || a.code_identifier || '').toLowerCase()
    const matchSearch = !search || name.includes(search.toLowerCase()) ||
      (a.framework || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.repo_name || '').toLowerCase().includes(search.toLowerCase())
    const matchFramework = !frameworkFilter || a.framework === frameworkFilter
    const matchPlatform = !platformFilter || a.runtime_platform === platformFilter
    // INT-6 — "Missing intent" filter for fleet-wide intent-gap audit
    const matchIntent = !missingIntentOnly || !a.latest_goal_preview
    return matchSearch && matchFramework && matchPlatform && matchIntent
  })

  const missingIntentCount = agents.filter((a: any) => !a.latest_goal_preview).length

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Registry</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {view === 'agents'
              ? 'Tenant-wide authoritative agent identities across all repos'
              : 'The stores, indexes and corpora your agents depend on, across all repos'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {count !== null && (
            <Chip
              icon={<SmartToy fontSize="small" />}
              label={`${count} active agent${count !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
            />
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAgents} size="small"><Refresh /></IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs
        value={view}
        onChange={(_e, v) => v && setView(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="agents" label="Agents" sx={{ textTransform: 'none' }} />
        <Tab value="components" label="Knowledge layer" sx={{ textTransform: 'none' }} />
      </Tabs>

      {view === 'components' && (
        <ComponentsView agents={agents} onSelectAgent={openDetail} />
      )}

      {view === 'agents' && <>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small" placeholder="Search by name, framework, repo…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="retired">Retired</MenuItem>
            <MenuItem value="merged">Merged</MenuItem>
          </Select>
        </FormControl>
        {frameworks.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Framework</InputLabel>
            <Select value={frameworkFilter} label="Framework" onChange={e => setFrameworkFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {frameworks.map((f: any) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {/* Gated independently of the framework filter: an estate can have
            platform-deployed agents whose frameworks were not recognised,
            and vice versa. */}
        {platforms.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Runtime platform</InputLabel>
            <Select value={platformFilter} label="Runtime platform" onChange={e => setPlatformFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {platforms.map((pf: any) => (
                <MenuItem key={pf} value={pf}>{String(pf).replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* INT-6 — Missing-intent filter for fleet-wide gap audit */}
        {missingIntentCount > 0 && (
          <Chip
            label={`Missing intent (${missingIntentCount})`}
            size="medium"
            variant={missingIntentOnly ? 'filled' : 'outlined'}
            color={missingIntentOnly ? 'warning' : 'default'}
            onClick={() => setMissingIntentOnly((v) => !v)}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <SmartToy sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No agents found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Agents appear here after their first scan.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Agent</strong></TableCell>
                <TableCell><strong>Intent</strong></TableCell>
                <TableCell><strong>Framework</strong></TableCell>
                <TableCell><strong>Repo</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Connectivity</strong></TableCell>
                <TableCell><strong>Last Seen</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((agent: any) => (
                <TableRow key={agent.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {agent.display_name || agent.code_identifier || agent.id.substring(0, 8)}
                      </Typography>
                      {agent.code_identifier && agent.display_name && agent.code_identifier !== agent.display_name && (
                        <Typography variant="caption" color="text.secondary">
                          {agent.code_identifier}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {/* INT-6 — Declared intent column. Pulls from
                      AgentRegistry.latest_goal_preview which the
                      IdentityResolver populates on each scan. */}
                  <TableCell sx={{ maxWidth: 240 }}>
                    {agent.latest_goal_preview ? (
                      <Tooltip title={agent.latest_goal_preview} placement="top">
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 220,
                            fontSize: '0.78rem',
                          }}
                        >
                          {agent.latest_goal_preview}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Tooltip title="No declared goal text found on the most recent scan. Consider adding a role/goal description to the agent definition for governance clarity.">
                        <Chip
                          label="Intent not declared"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {agent.framework ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={agent.framework}
                          size="small"
                          variant="filled"
                          color={isSpecDefined(agent) ? 'secondary' : 'default'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                        {/* Runtime platform is a separate axis from framework:
                            framework = how the agent is built, platform = where
                            it runs. Rendered outlined so the two read as
                            different kinds of fact. Absent platform means
                            self-hosted, which is a valid answer — so nothing is
                            shown rather than "unknown". */}
                        {agent.runtime_platform && (
                          <Tooltip title={`Runtime platform: ${agent.runtime_platform.replace(/_/g, ' ')}`}>
                            <Chip
                              label={agent.runtime_platform.replace(/_/g, ' ')}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                        {isSpecDefined(agent) && (
                          <Tooltip title="Spec-defined agent (markdown / YAML / JSON)">
                            <Chip
                              label="spec"
                              size="small"
                              color="secondary"
                              sx={{ fontSize: '0.6rem', height: 18 }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {agent.repo_name || '—'}
                    </Typography>
                    {agent.file_name && (
                      <Typography variant="caption" color="text.secondary">{agent.file_name}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={statusIcon(agent.status) || undefined}
                      label={agent.status}
                      color={statusColor(agent.status) as any}
                      size="small"
                      sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <ConnectivityChips agent={agent} />
                  </TableCell>
                  <TableCell>
                    {agent.last_seen_at ? (
                      <Tooltip title={new Date(agent.last_seen_at).toLocaleString()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(agent.last_seen_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View identity history">
                      <IconButton size="small" onClick={() => openDetail(agent)}>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* The fetch above pulls every page so search and the dropdowns work
              over the whole estate; this paginates the render only. 169 agents
              in one unbroken table is a scroll, not a view. */}
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </TableContainer>
      )}

      {/* Detail Dialog */}
      </>}

      {/* The detail dialog belongs to the agents view; a component has no
          per-row drawer here, because what a component *is* gets answered on
          the agent card and who depends on it by the Blast Radius lens. */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy />
            {selected?.display_name || selected?.code_identifier || 'Agent Detail'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : detailData ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Registry ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {detailData.id}
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">Framework</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                    <Typography variant="body2">{detailData.framework || '—'}</Typography>
                    {isSpecDefined(detailData) && (
                      <Chip label="spec-defined" size="small" color="secondary" sx={{ fontSize: '0.6rem', height: 18 }} />
                    )}
                  </Stack>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.25 }}>
                    <Chip label={detailData.status} color={statusColor(detailData.status) as any} size="small" sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }} />
                  </Box>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">Repo</Typography>
                  <Typography variant="body2">{detailData.repo_name || '—'}</Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">File</Typography>
                  <Typography variant="body2">{detailData.file_path || detailData.file_name || '—'}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">First Seen</Typography>
                  <Typography variant="body2">
                    {detailData.first_seen_at ? new Date(detailData.first_seen_at).toLocaleDateString() : '—'}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">Last Seen</Typography>
                  <Typography variant="body2">
                    {detailData.last_seen_at ? new Date(detailData.last_seen_at).toLocaleString() : '—'}
                  </Typography>
                </Box>
              </Stack>

              {/* AR4 — Latest scan snapshot: connectivity + cert + verdict
                  from the most-recent scan, already denormalized onto the
                  registry row. No second round trip required. */}
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>Latest scan snapshot</Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Connectivity
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <ConnectivityChips agent={detailData} />
                    </Box>
                  </Box>
                  {detailData.certificate && (
                    <Stack direction="row" spacing={2}>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">Drift verdict</Typography>
                        <Box sx={{ mt: 0.25 }}>
                          <Chip
                            label={(detailData.certificate.latest_drift_verdict || 'STABLE').replace('_', ' ').toLowerCase()}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              bgcolor: driftVerdictColor(detailData.certificate.latest_drift_verdict),
                              color: 'white',
                            }}
                          />
                          {detailData.certificate.latest_improvement === 'POSITIVE_DRIFT' && (
                            <Chip
                              label="improved"
                              size="small"
                              sx={{ ml: 0.5, fontSize: '0.65rem', bgcolor: '#388e3c', color: 'white' }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">Certificate</Typography>
                        <Box sx={{ mt: 0.25 }}>
                          <Chip
                            label={detailData.certificate.status}
                            size="small"
                            color={statusColor(detailData.certificate.status) as any}
                            sx={{ fontSize: '0.65rem', textTransform: 'capitalize' }}
                          />
                          {detailData.certificate.expires_at && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              expires {new Date(detailData.certificate.expires_at).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Identity Match History */}
              {detailData.identity_matches?.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Identity Resolution History</Typography>
                    <Stack spacing={1}>
                      {detailData.identity_matches.map((m: any) => (
                        <Box key={m.id} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Chip
                              label={matchTypeLabel[m.match_type] || m.match_type}
                              color={matchTypeColor(m.match_type)}
                              size="small"
                              sx={{ fontSize: '0.65rem' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {m.match_confidence != null ? `${(m.match_confidence * 100).toFixed(0)}% confidence` : ''}
                            </Typography>
                          </Stack>
                          {m.match_reasons?.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {m.match_reasons.join(' · ')}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.disabled">
                            {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
              {/* ARIAS Exposure — what a compromise of this agent would
                  reach. Placed beside the topology deep link because both key
                  on the same hint, and both degrade the same way when the
                  registry's stored hint predates the current scan. */}
              {/* `exposureByHint` is null when the route refused, so guarding on
                  it alone hid the whole block and the tenant could not tell
                  Exposure was off. */}
              {(exposureByHint || exposureRefused) && detailData?.agent_id_hint && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    If compromised
                  </Typography>
                  <AgentBlastRadius
                    exposure={exposureByHint?.[detailData.agent_id_hint] ?? null}
                    loading={exposureLoading}
                    notEntitled={exposureRefused}
                  />
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {/* AR3 — deep link to /topology?focus=<agent_id_hint>. Only
              shown when the registry row has a known hint (most do;
              rows from older scanner binaries before AR1 may not). */}
          {detailData?.agent_id_hint && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/topology?focus=${encodeURIComponent(detailData.agent_id_hint)}`)}
            >
              View in topology
            </Button>
          )}
          {/* P1b deep link — open the per-agent drift timeline page. */}
          {detailData?.certificate?.id && (
            <Button
              variant="contained"
              onClick={() => navigate(`/certificates/${detailData.certificate.id}`)}
            >
              View drift timeline
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}
